import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import IconBtn from '../../components/ui/IconBtn';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Badge from '../../components/ui/Badge';
import Secao from '../../components/ui/Secao';
import { propostasApi } from '../../api/vendas';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';
import { useAuth } from '../../context/AuthContext';
import PropostaForm, { OPCOES_SITUACAO, OPCOES_CATEGORIA } from './PropostaForm';
import { STATUS_TOM as STATUS_TOM_PEDIDO, STATUS_LABEL as STATUS_LABEL_PEDIDO, statusPropostaExibicao } from './PedidoDetail';

const SITUACAO_LABEL = Object.fromEntries(OPCOES_SITUACAO.map(o => [o.value, o.label]));
const CATEGORIA_LABEL = Object.fromEntries(OPCOES_CATEGORIA.map(o => [o.value, o.label]));

function dataValidadeFinal(criadoEm, validadeDias) {
  if (!validadeDias) return null;
  const d = new Date(criadoEm);
  d.setDate(d.getDate() + Number(validadeDias));
  return d;
}

// Tudo é obrigatório pra poder efetivar, menos 2º Vendedor e as duas Observações.
function camposFaltando(proposta) {
  const faltando = [];
  if (!proposta.condicao_pagamento_id && !proposta.vale_mercadoria_funcionario_id) faltando.push('Condição de Pagamento (ou Vale Mercadoria)');
  if (!proposta.situacao) faltando.push('Situação');
  if (!proposta.categoria) faltando.push('Categoria');
  if (!proposta.validade_dias) faltando.push('Validade da Proposta');
  if (!proposta.entrega_logradouro) faltando.push('Logradouro (Entrega)');
  if (!proposta.entrega_numero) faltando.push('Número (Entrega)');
  if (!proposta.entrega_bairro) faltando.push('Bairro (Entrega)');
  if (!proposta.entrega_cidade) faltando.push('Cidade (Entrega)');
  if (!proposta.entrega_uf) faltando.push('UF (Entrega)');
  if (proposta.tipo_frete === 'cif' && !proposta.entrega_propria && !proposta.transportadora_id) faltando.push('Transportadora (ou marcar Entrega Própria)');
  if (!proposta.itens || proposta.itens.length === 0) faltando.push('Itens');
  return faltando;
}

export default function PropostaDetail({ propostaId, onVoltar, onAbrirPedido }) {
  const { empresaAtual, usuario } = useAuth();
  const [proposta, setProposta] = useState(null);
  const [erro, setErro] = useState('');
  const [processando, setProcessando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [confirmando, setConfirmando] = useState(null); // null | 'cancelar' | 'excluir' | 'efetivar' | 'negociar'

  const carregar = () => propostasApi.obter(propostaId).then(setProposta).catch(() => {});
  useEffect(() => { carregar(); }, [propostaId]); // eslint-disable-line

  useTituloPagina(proposta ? `Proposta #${proposta.numero}` : 'Proposta');

  const cancelar = async () => {
    setProcessando(true);
    try {
      await propostasApi.cancelar(propostaId);
      setConfirmando(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const excluir = async () => {
    setProcessando(true);
    try {
      await propostasApi.excluir(propostaId);
      setConfirmando(null);
      onVoltar();
    } catch (e) {
      setErro(e.message);
      setConfirmando(null);
    } finally {
      setProcessando(false);
    }
  };

  const converter = async () => {
    setProcessando(true);
    setErro('');
    try {
      const resultado = await propostasApi.converter(propostaId);
      setConfirmando(null);
      carregar();
      if (onAbrirPedido) onAbrirPedido(resultado.id);
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const enviarParaNegociacao = async () => {
    setProcessando(true);
    setErro('');
    try {
      await propostasApi.enviarNegociacao(propostaId);
      setConfirmando(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const imprimir = () => {
    const subtotalItem = (i) => Number(i.quantidade) * Number(i.valor_unitario) * (1 - (Number(i.desconto_pct) || 0) / 100) * (1 + (Number(i.ipi_pct) || 0) / 100);
    const totalGeral = proposta.itens.reduce((s, i) => s + subtotalItem(i), 0);
    const validade = dataValidadeFinal(proposta.criado_em, proposta.validade_dias);
    const enderecoEmpresa = [empresaAtual?.municipio, empresaAtual?.uf].filter(Boolean).join('/');
    const janela = window.open('', '_blank');
    janela.document.write(`<!DOCTYPE html><html><head><title>Proposta #${proposta.numero}</title>
      <meta charset="utf-8" />
      <style>
        body { font-family: Arial, sans-serif; color: #0f172a; padding: 24px; }
        h2 { margin: 0; font-size: 18px; }
        h3 { font-size: 13px; margin-bottom: 6px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 16px; }
        th { text-align: left; border-bottom: 1.5px solid #000; padding: 4px 6px; }
        td { padding: 4px 6px; border-bottom: 1px solid #ccc; }
        .cabecalho { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 16px; }
        .direita { text-align: right; }
        .bloco { font-size: 12px; margin-bottom: 16px; line-height: 1.6; }
        .total-row td { background: #EFF6FF; font-weight: 700; padding: 8px 6px; border-bottom: none; }
        .rodape { margin-top: 28px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 10.5px; color: #555; text-align: center; line-height: 1.6; }
        .aviso-fiscal { text-align: center; font-size: 11px; font-weight: 700; color: #555; margin-top: 4px; }
        .dados-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 16px; font-size: 12px; margin-bottom: 16px; }
        .dados-grid .rotulo { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: .03em; }
      </style>
      </head><body>
      <div class="cabecalho">
        <div style="display:flex; align-items:flex-start; gap:12px;">
          ${empresaAtual?.logo_url ? `<img src="${window.location.origin}${empresaAtual.logo_url}" alt="Logo" style="max-width:90px; max-height:70px; object-fit:contain;" />` : ''}
          <div>
            <h2>${empresaAtual?.nome || 'Empresa'}</h2>
            ${empresaAtual?.razao_social ? `<div style="font-size:12px">${empresaAtual.razao_social}</div>` : ''}
            ${empresaAtual?.cnpj ? `<div style="font-size:12px">CNPJ: ${empresaAtual.cnpj}</div>` : ''}
            ${empresaAtual?.chave_pix ? `<div style="font-size:12px">Chave Pix: ${empresaAtual.chave_pix}</div>` : ''}
            ${empresaAtual?.banco ? `<div style="font-size:12px">Banco: ${empresaAtual.banco}${empresaAtual.agencia ? ` — Ag: ${empresaAtual.agencia}` : ''}${empresaAtual.conta ? ` — Conta: ${empresaAtual.conta}${empresaAtual.digito ? `-${empresaAtual.digito}` : ''}` : ''}</div>` : ''}
          </div>
        </div>
        <div class="direita">
          <h2>Proposta #${proposta.numero}</h2>
          <div style="font-size:12px">Data: ${new Date(proposta.criado_em).toLocaleDateString('pt-BR')}</div>
          <div style="font-size:12px">Validade: ${validade ? validade.toLocaleDateString('pt-BR') : '-'}</div>
          <div style="font-size:12px">Vendedor: ${proposta.vendedor_nome}${proposta.segundo_vendedor_nome ? ` / ${proposta.segundo_vendedor_nome}` : ''}</div>
        </div>
      </div>

      <h3>Dados Adicionais</h3>
      <div class="dados-grid">
        <div><div class="rotulo">Situação</div>${SITUACAO_LABEL[proposta.situacao] || '-'}</div>
        <div><div class="rotulo">Categoria</div>${CATEGORIA_LABEL[proposta.categoria] || '-'}</div>
        <div><div class="rotulo">Frete</div>${proposta.tipo_frete === 'cif' ? 'CIF' : 'FOB'}</div>
        <div><div class="rotulo">Transportadora</div>${proposta.entrega_propria ? 'Entrega Própria' : (proposta.transportadora_nome || '-')}</div>
        <div><div class="rotulo">Condição de Pagamento</div>${proposta.vale_mercadoria_funcionario_id ? `Vale Mercadoria — ${proposta.vale_mercadoria_funcionario_nome}` : (proposta.condicao_pagamento_nome || '-')}</div>
        <div><div class="rotulo">Prazo de Entrega</div>${proposta.prazo_entrega_dias ? `${proposta.prazo_entrega_dias} dia${proposta.prazo_entrega_dias === 1 ? '' : 's'}` : '-'}</div>
      </div>

      <h3>Cliente</h3>
      <div class="bloco">
        <div><strong>${proposta.cliente_nome}</strong></div>
        <div>${proposta.cliente_contato_nome ? `Contato: ${proposta.cliente_contato_nome}` : ''}${proposta.cliente_telefone ? `  ·  Tel: ${proposta.cliente_telefone}` : ''}</div>
        <div>${proposta.end_principal_logradouro || ''}${proposta.end_principal_numero ? `, ${proposta.end_principal_numero}` : ''}${proposta.end_principal_bairro ? ` — ${proposta.end_principal_bairro}` : ''}${proposta.end_principal_cidade ? `, ${proposta.end_principal_cidade}` : ''}${proposta.end_principal_uf ? `/${proposta.end_principal_uf}` : ''}</div>
      </div>

      <h3>Endereço de Entrega</h3>
      <div class="bloco">
        <div>${proposta.entrega_logradouro || ''}${proposta.entrega_numero ? `, ${proposta.entrega_numero}` : ''}${proposta.entrega_complemento ? ` — ${proposta.entrega_complemento}` : ''}</div>
        <div>${proposta.entrega_bairro || ''}${proposta.entrega_cidade ? `, ${proposta.entrega_cidade}` : ''}${proposta.entrega_uf ? `/${proposta.entrega_uf}` : ''}${proposta.entrega_cep ? ` — CEP ${proposta.entrega_cep}` : ''}</div>
      </div>

      <h3>Itens</h3>
      <table>
        <thead><tr><th>Produto</th><th>Quantidade</th><th>Valor Unit.</th><th>Desc. %</th><th>IPI %</th><th>Subtotal</th></tr></thead>
        <tbody>
          ${proposta.itens.map(item => `<tr>
            <td>${item.produto_codigo} - ${item.produto_nome}</td>
            <td>${Number(item.quantidade).toLocaleString('pt-BR')}</td>
            <td>R$ ${Number(item.valor_unitario).toFixed(2)}</td>
            <td>${Number(item.desconto_pct) > 0 ? `${Number(item.desconto_pct)}%` : '-'}</td>
            <td>${Number(item.ipi_pct) > 0 ? `${Number(item.ipi_pct)}%` : '-'}</td>
            <td>R$ ${subtotalItem(item).toFixed(2)}</td>
          </tr>`).join('')}
        </tbody>
        <tfoot>
          <tr class="total-row"><td colspan="5" style="text-align:right">Total</td><td>R$ ${totalGeral.toFixed(2)}</td></tr>
        </tfoot>
      </table>

      ${proposta.observacoes ? `<h3>Observações</h3><p style="font-size:12px">${proposta.observacoes}</p>` : ''}

      <div class="aviso-fiscal">Documento Sem Valor Fiscal</div>
      <div class="rodape">
        <strong>${empresaAtual?.razao_social || empresaAtual?.nome || ''}</strong>${empresaAtual?.cnpj ? ` — CNPJ: ${empresaAtual.cnpj}` : ''}${enderecoEmpresa ? ` — ${enderecoEmpresa}` : ''}
      </div>
      </body></html>`);
    janela.document.close();
    janela.onload = () => { janela.focus(); janela.print(); };
  };

  if (editando) {
    return (
      <PropostaForm
        propostaId={propostaId}
        onVoltar={() => setEditando(false)}
        onSalvo={() => { setEditando(false); carregar(); }}
      />
    );
  }

  if (!proposta) return null;

  const subtotalItem = (i) => Number(i.quantidade) * Number(i.valor_unitario) * (1 - (Number(i.desconto_pct) || 0) / 100) * (1 + (Number(i.ipi_pct) || 0) / 100);
  const totalGeral = proposta.itens.reduce((s, i) => s + subtotalItem(i), 0);
  const validade = dataValidadeFinal(proposta.criado_em, proposta.validade_dias);
  const pedidoAtivo = proposta.pedidos.find(p => p.status !== 'cancelado');
  const pedidoDevolvido = proposta.pedidos.find(p => p.status === 'devolvido');
  const faltando = camposFaltando(proposta);
  const podeEfetivar = faltando.length === 0;
  const exibicaoStatus = statusPropostaExibicao(proposta.status, pedidoAtivo?.status);
  const semRestricaoPreco = usuario?.perfil !== 'vendedor' || usuario?.autorizado_tabela_completa;
  const itensNegociacao = semRestricaoPreco ? [] : proposta.itens.filter(i =>
    i.margem_promocao_valor != null && Number(i.valor_unitario) < Number(i.margem_promocao_valor) && Number(i.valor_unitario) !== Number(i.valor_aprovado_gerente)
  );

  const iniciarEfetivacao = () => setConfirmando(itensNegociacao.length > 0 ? 'negociar' : 'efetivar');

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <strong style={{ fontSize: 15 }}>Cliente: {proposta.cliente_nome}</strong>
              <Badge tom={exibicaoStatus.tom}>{exibicaoStatus.label}</Badge>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variante="ghost" onClick={onVoltar}>Voltar</Btn>
              <IconBtn titulo="Imprimir Proposta" variante="ghost" onClick={imprimir}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
              </IconBtn>
              {proposta.status === 'aberta' && (
                <>
                  <IconBtn titulo="Editar Proposta" variante="secondary" onClick={() => setEditando(true)}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
                    </svg>
                  </IconBtn>
                  <IconBtn titulo="Cancelar Proposta" variante="danger" onClick={() => setConfirmando('cancelar')}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="m15 9-6 6M9 9l6 6" />
                    </svg>
                  </IconBtn>
                  {proposta.pedidos.length === 0 && (
                    <IconBtn titulo="Excluir Proposta" variante="danger" onClick={() => setConfirmando('excluir')}>
                      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </IconBtn>
                  )}
                  <IconBtn
                    titulo={!podeEfetivar ? `Faltam preencher: ${faltando.join(', ')}` : 'Efetivar Proposta'}
                    variante="primary"
                    disabled={processando || !podeEfetivar}
                    onClick={iniciarEfetivacao}
                  >
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </IconBtn>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 12, marginTop: 10 }}>
            <div><span style={{ color: COLORS.textLight }}>Nº Proposta:</span> <strong>#{proposta.numero}</strong></div>
            <div><span style={{ color: COLORS.textLight }}>Nº Pedido:</span> <strong>{pedidoAtivo ? `#${pedidoAtivo.numero}` : '-'}</strong></div>
            <div><span style={{ color: COLORS.textLight }}>Nº NF:</span> <strong>{proposta.numero_nota_fiscal || '-'}</strong></div>
            <div><span style={{ color: COLORS.textLight }}>Data:</span> <strong>{new Date(proposta.criado_em).toLocaleDateString('pt-BR')}</strong></div>
          </div>
        </div>

        {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}

        {pedidoDevolvido && (
          <div style={{ background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}40`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.danger, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Pedido #{pedidoDevolvido.numero} devolvido pelo almoxarifado
            </div>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.text }}>{pedidoDevolvido.observacao_devolucao}</p>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: COLORS.textMid }}>
              Ajuste o que for necessário e efetive a proposta de novo — o pedido continuará com o mesmo número.
            </p>
          </div>
        )}

        {proposta.negociacao_observacao && (
          <div style={{ background: COLORS.warningBg, border: `1px solid ${COLORS.warning}66`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Observação do Gestor (Negociação)
            </div>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.text }}>{proposta.negociacao_observacao}</p>
          </div>
        )}

        {proposta.status === 'aberta' && !podeEfetivar && (
          <p style={{ color: COLORS.warning, fontSize: 12, marginTop: -8, marginBottom: 14 }}>
            Pra efetivar, preencha antes: {faltando.join(', ')}.
          </p>
        )}

        <Secao titulo="Dados da Proposta">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 13 }}>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>Vendedor</div>{proposta.vendedor_nome}</div>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>2º Vendedor</div>{proposta.segundo_vendedor_nome || '-'}</div>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>Validade</div>{validade ? validade.toLocaleDateString('pt-BR') : '-'}</div>
            <div>
              <div style={{ color: COLORS.textMid, fontSize: 11 }}>Condição de Pagamento</div>
              {proposta.vale_mercadoria_funcionario_id
                ? <span><Badge tom="aviso">Vale Mercadoria</Badge> {proposta.vale_mercadoria_funcionario_nome}</span>
                : (proposta.condicao_pagamento_nome || '-')}
            </div>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>Situação</div>{SITUACAO_LABEL[proposta.situacao] || '-'}</div>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>Categoria</div>{CATEGORIA_LABEL[proposta.categoria] || '-'}</div>
            <div>
              <div style={{ color: COLORS.textMid, fontSize: 11 }}>Frete</div>
              {proposta.tipo_frete === 'cif' ? `CIF${proposta.entrega_propria ? ' — Entrega Própria' : proposta.transportadora_nome ? ` — ${proposta.transportadora_nome}` : ''}` : 'FOB'}
            </div>
            <div>
              <div style={{ color: COLORS.textMid, fontSize: 11 }}>Prazo de Entrega</div>
              {proposta.prazo_entrega_dias ? `${proposta.prazo_entrega_dias} dia${proposta.prazo_entrega_dias === 1 ? '' : 's'}` : '-'}
            </div>
          </div>
        </Secao>

        <div style={{ height: 14 }} />

        <Secao titulo="Cliente">
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div>{proposta.cliente_contato_nome ? `Contato: ${proposta.cliente_contato_nome}` : ''}{proposta.cliente_telefone ? `  ·  Tel: ${proposta.cliente_telefone}` : ''}{proposta.cliente_contato_email ? `  ·  ${proposta.cliente_contato_email}` : ''}</div>
            <div>Endereço: {proposta.end_principal_logradouro || '-'}{proposta.end_principal_numero ? `, ${proposta.end_principal_numero}` : ''}{proposta.end_principal_bairro ? ` — ${proposta.end_principal_bairro}` : ''}{proposta.end_principal_cidade ? `, ${proposta.end_principal_cidade}` : ''}{proposta.end_principal_uf ? `/${proposta.end_principal_uf}` : ''}</div>
          </div>
        </Secao>

        <div style={{ height: 14 }} />

        <Secao titulo="Endereço de Entrega">
          <div style={{ fontSize: 13 }}>
            {proposta.entrega_logradouro || '-'}{proposta.entrega_numero ? `, ${proposta.entrega_numero}` : ''}{proposta.entrega_complemento ? ` — ${proposta.entrega_complemento}` : ''}
            {proposta.entrega_bairro ? ` — ${proposta.entrega_bairro}` : ''}{proposta.entrega_cidade ? `, ${proposta.entrega_cidade}` : ''}{proposta.entrega_uf ? `/${proposta.entrega_uf}` : ''}
            {proposta.entrega_cep ? ` — CEP ${proposta.entrega_cep}` : ''}
          </div>
        </Secao>

        <div style={{ height: 14 }} />

        <Secao titulo="Itens">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: COLORS.bg }}>
                <th style={{ padding: 8 }}>Produto</th>
                <th style={{ padding: 8 }}>Quantidade</th>
                <th style={{ padding: 8 }}>Valor Unit.</th>
                <th style={{ padding: 8 }}>Desc. %</th>
                <th style={{ padding: 8 }}>IPI %</th>
                <th style={{ padding: 8 }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {proposta.itens.map(item => {
                const abaixoPromocao = item.margem_promocao_valor != null && Number(item.valor_unitario) < Number(item.margem_promocao_valor);
                return (
                <tr key={item.id} style={{ borderTop: `1px solid ${COLORS.border}`, background: abaixoPromocao ? COLORS.warningBg : undefined }}
                  title={abaixoPromocao ? `Abaixo da tabela Promoção (R$ ${Number(item.margem_promocao_valor).toFixed(2)})` : undefined}>
                  <td style={{ padding: 8 }}>{item.produto_codigo} - {item.produto_nome}</td>
                  <td style={{ padding: 8 }}>{Number(item.quantidade).toLocaleString('pt-BR')}</td>
                  <td style={{ padding: 8, fontWeight: abaixoPromocao ? 700 : 400, color: abaixoPromocao ? '#92400E' : undefined }}>R$ {Number(item.valor_unitario).toFixed(2)}</td>
                  <td style={{ padding: 8 }}>{Number(item.desconto_pct) > 0 ? `${Number(item.desconto_pct)}%` : '-'}</td>
                  <td style={{ padding: 8 }}>{Number(item.ipi_pct) > 0 ? `${Number(item.ipi_pct)}%` : '-'}</td>
                  <td style={{ padding: 8 }}>R$ {subtotalItem(item).toFixed(2)}</td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: COLORS.primaryBg }}>
                <td colSpan={5} style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: COLORS.primary }}>Total</td>
                <td style={{ padding: 8, fontWeight: 700, color: COLORS.primary }}>R$ {totalGeral.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </Secao>

        {proposta.pedidos.length > 0 && (
          <>
            <div style={{ height: 14 }} />
            <Secao titulo="Pedidos Gerados">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {proposta.pedidos.map(p => (
                  <div key={p.id} onClick={() => onAbrirPedido(p.id)}
                    style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: 'pointer' }}>
                    <span>Pedido #{p.numero}</span>
                    <Badge tom={STATUS_TOM_PEDIDO[p.status]}>{STATUS_LABEL_PEDIDO[p.status]}</Badge>
                  </div>
                ))}
              </div>
            </Secao>
          </>
        )}

        {proposta.observacoes && (
          <>
            <div style={{ height: 14 }} />
            <Secao titulo="Observações">
              <p style={{ fontSize: 13, color: COLORS.text, margin: 0 }}>{proposta.observacoes}</p>
            </Secao>
          </>
        )}

        {proposta.observacoes_internas && (
          <>
            <div style={{ height: 14 }} />
            <Secao titulo="Observações Internas">
              <p style={{ fontSize: 13, color: COLORS.text, margin: 0 }}>{proposta.observacoes_internas}</p>
            </Secao>
          </>
        )}
      </Card>

      {confirmando === 'cancelar' && (
        <ConfirmModal
          titulo="Cancelar Proposta"
          mensagem="Tem certeza que quer cancelar esta proposta? Essa ação não pode ser desfeita."
          textoConfirmar="Cancelar Proposta"
          textoCancelar="Voltar"
          variante="danger"
          processando={processando}
          onConfirmar={cancelar}
          onCancelar={() => setConfirmando(null)}
        />
      )}

      {confirmando === 'excluir' && (
        <ConfirmModal
          titulo="Excluir Proposta"
          mensagem="Tem certeza que quer excluir esta proposta? Diferente de cancelar, ela some do sistema — não vai ficar registrada no histórico. Essa ação não pode ser desfeita."
          textoConfirmar="Excluir Proposta"
          textoCancelar="Voltar"
          variante="danger"
          processando={processando}
          onConfirmar={excluir}
          onCancelar={() => setConfirmando(null)}
        />
      )}

      {confirmando === 'efetivar' && (
        <ConfirmModal
          titulo="Efetivar Proposta"
          mensagem="Confirma efetivar esta proposta? Ela vai virar Pedido e a quantidade dos itens será reservada no estoque."
          textoConfirmar="Efetivar Proposta"
          textoCancelar="Voltar"
          variante="primary"
          processando={processando}
          onConfirmar={converter}
          onCancelar={() => setConfirmando(null)}
        />
      )}

      {confirmando === 'negociar' && (
        <ConfirmModal
          titulo="Preço Abaixo da Tabela Promoção"
          mensagem={`Item(ns) com preço abaixo da tabela Promoção: ${itensNegociacao.map(i => `${i.produto_codigo} - ${i.produto_nome} (R$ ${Number(i.valor_unitario).toFixed(2)}, Promoção R$ ${Number(i.margem_promocao_valor).toFixed(2)})`).join('; ')}. Essa proposta precisa da aprovação do gestor antes de virar Pedido.`}
          textoConfirmar="Enviar para Negociação"
          textoCancelar="Voltar"
          variante="primary"
          processando={processando}
          onConfirmar={enviarParaNegociacao}
          onCancelar={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
